import "../../utilities/generate_uuid.js";
import "./spotlight/items_list.js";
// item.js imports items_list.js itself, but we import it explicitly here
// to ensure it's included in the bundle.
import "./spotlight/items_list/item.js";
import "./spotlight/item_details.js";
import "./spotlight/status_bar.js";

window.Sparkle ??= {};

// Sub-modules (items_list.js, item_details.js, status_bar.js) are imported
// above and attach themselves to `Sparkle.Spotlight`. The class assignment
// below would otherwise blow that namespace object away — preserve it so
// `Spotlight.ItemsList` etc. remain accessible after the class is defined.
const __previousSpotlightNamespace = window.Sparkle.Spotlight;

Sparkle.Spotlight = class Spotlight {
  constructor(options) {
    this.render = this.render.bind(this);
    this.handleActivedescendantChanged = this.handleActivedescendantChanged.bind(this);
    this.handleClickItem = this.handleClickItem.bind(this);
    this.handlePointerOverItem = this.handlePointerOverItem.bind(this);
    this.handleMouseleave = this.handleMouseleave.bind(this);
    this.handleUpPressed = this.handleUpPressed.bind(this);
    this.handleDownPressed = this.handleDownPressed.bind(this);
    this.handleEnterPressed = this.handleEnterPressed.bind(this);
    this.setItems = this.setItems.bind(this);
    this.focusPrevious = this.focusPrevious.bind(this);
    this.focusNext = this.focusNext.bind(this);
    this.focusLast = this.focusLast.bind(this);
    this.clearFocus = this.clearFocus.bind(this);
    this.setFocussed = this.setFocussed.bind(this);
    this.selectFocussed = this.selectFocussed.bind(this);
    this.isFocussed = this.isFocussed.bind(this);
    this.getFocussed = this.getFocussed.bind(this);
    this.selectedItem = this.selectedItem.bind(this);
    this.isVisible = this.isVisible.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.destroy = this.destroy.bind(this);
    this._selectItem = this._selectItem.bind(this);
    this._setKeyHandler = this._setKeyHandler.bind(this);
    this._triggerEvent = this._triggerEvent.bind(this);
    if (options == null) {
      options = {};
    }
    this.options = jQuery.extend(
      {
        spotlightId: Sparkle.Utilities.generateUUID(),
        details: true, // Show the details panel
        status: true, // Show the status bar
        autofocus: true, // Automatically focus the first item
        unfocus: true, // Allow the results to be unfocussed by navigating up past the first result
        identifier: true, // Show the unique identifier for the resource
        listClass: Sparkle.Spotlight.ItemsList, // Override the class used for the list
        itemClass: undefined, // Override the class used for the items in the list
        showOnInit: true, // Show spotlight when initialized
      },
      options,
    );

    this.el = jQuery("<div>", { class: "spotlight" }).toggleClass("with-details", this.options.details);
    this.itemsList = new this.options.listClass([], {
      itemClass: this.options.itemClass,
      spotlightId: this.options.spotlightId,
    });
    this.itemsListContainer = jQuery('<div class="spotlight-items-list-container">');

    this.itemDetails = new Sparkle.Spotlight.ItemDetails();
    this.itemDetailsContainer = jQuery('<div class="spotlight-item-details-container">');

    this.statusBar = new Sparkle.Spotlight.StatusBar();
    this.statusBarContainer = jQuery('<div class="spotlight-status-bar-container">');
    this.statusBar.setShortcuts("Use Up/Down arrow keys to navigate. Press Enter to select an item.");

    this.showing = !!this.options.showOnInit;

    this.itemsData = [];
    this.focussedIndex = 0;
    this.minFocussedIndex = this.options.unfocus ? -1 : 0;

    // BEHAVIOUR

    jQuery(this.itemsList).on("clickitem", this.handleClickItem);
    jQuery(this.itemsList).on("pointeroveritem", this.handlePointerOverItem);
    jQuery(this.itemsList).on("activedescendantChanged", this.handleActivedescendantChanged);
    this.el.on("mouseleave", this.handleMouseleave);
    this._setKeyHandler(13, this.handleEnterPressed); // enter
    this._setKeyHandler(38, this.handleUpPressed); // up
    this._setKeyHandler(40, this.handleDownPressed); // down
  }

  render() {
    this.el.append(this.itemsListContainer.append(this.itemsList.render().el));
    if (this.options.details) {
      this.el.append(this.itemDetailsContainer.append(this.itemDetails.render().el));
    }
    if (this.options.status) {
      this.el.append(this.statusBarContainer.append(this.statusBar.render().el));
    }
    this.el.toggle(this.showing);

    return this;
  }

  // EVENT HANDLERS
  handleActivedescendantChanged(event, activeId) {
    return this._triggerEvent("activedescendantChanged", activeId);
  }

  handleClickItem(event, listItem) {
    return this._selectItem(listItem.data);
  }

  // Moving the pointer onto a result takes the selection, so enter acts on the result under it and
  // only that result is highlighted. The list is not scrolled to it, which would move a different
  // result under a pointer that had not moved.
  handlePointerOverItem(event, listItem) {
    this.focusOwner = "pointer";
    if (listItem.index === this.focussedIndex) {
      return;
    }

    return this.setFocussed(listItem.index, false);
  }

  // Only a selection the pointer made is given up when the pointer leaves; one the keyboard made
  // outlives it, so arrowing to a result and then moving the mouse away still opens that result on
  // enter. The list stays where it is on the way out.
  handleMouseleave(event) {
    if (this.focusOwner === "pointer") {
      return this.clearFocus({ resetScroll: false });
    }
  }

  handleUpPressed(event) {
    if (!this.isVisible()) {
      return;
    }
    this.focusPrevious();
    return event.preventDefault();
  }

  handleDownPressed(event) {
    if (!this.isVisible()) {
      return;
    }
    this.focusNext();
    return event.preventDefault();
  }

  handleEnterPressed(event) {
    const focussed = this.getFocussed();
    if (!this.isVisible() || !focussed) {
      return;
    }
    this._selectItem(focussed);
    return event.preventDefault();
  }

  // HELPER FUNCTIONS

  setItems(itemsData, statusMessage) {
    let autofocus;
    this.itemsData = itemsData;
    if (statusMessage == null) {
      statusMessage = this.itemsData.length === 1 ? "1 Item" : `${this.itemsData.length} Items`;
    }
    this.statusBar.setMessage(statusMessage);
    this.itemsList.setItems(this.itemsData, this.options.identifier);

    if (typeof this.options.autofocus === "function") {
      autofocus = this.options.autofocus();
    } else {
      ({ autofocus } = this.options);
    }

    if (autofocus) {
      this.focusOwner = null;
      this.setFocussed(0);
    } else {
      this.clearFocus();
    }

    this.hasItems = !!this.itemsData.length;
    return (this.isEmpty = !this.hasItems);
  }

  focusPrevious() {
    this.focusOwner = "keyboard";
    return this.setFocussed(this.focussedIndex - 1);
  }

  focusNext() {
    this.focusOwner = "keyboard";
    return this.setFocussed(this.focussedIndex + 1);
  }

  focusLast() {
    this.focusOwner = "keyboard";
    return this.setFocussed(this.itemsData.length - 1);
  }

  clearFocus({ resetScroll } = { resetScroll: true }) {
    this.focussedIndex = -1;
    this.focusOwner = null;
    this.itemsList.clearHighlight();
    if (resetScroll) {
      this.itemsList.resetScroll();
    }
    if (this.options.details) {
      return this.itemDetails.clearItem();
    }
  }

  setFocussed(index, scrollTo = true) {
    this.focussedIndex = index < this.minFocussedIndex ? this.itemsData.length - 1 : index % this.itemsData.length;
    this.itemsList.setHighlight(this.focussedIndex);
    if (scrollTo) {
      this.itemsList.scrollTo(this.focussedIndex);
    }
    if (this.options.details) {
      return this.itemDetails.setItem(this.getFocussed());
    }
  }

  selectFocussed() {
    if (this.isFocussed()) {
      return this._selectItem(this.getFocussed());
    }
  }

  isFocussed() {
    return !!this.getFocussed();
  }

  getFocussed() {
    return this.itemsData[this.focussedIndex];
  }

  selectedItem() {
    return this._selectedItem;
  }

  // Even if @showing is true, that just determines whether the dropdown is rendered. If the element's parent is visible, e.g. it's on a different tab, then we don't want to handle certain events.
  isVisible() {
    return this.el.is(":visible");
  }

  show(options = {}) {
    if (!this.showing) {
      this.showing = true;
      this.el.show();
      if (!options.silent) {
        this._triggerEvent("show");
      }
      // scrolls the focussed item into view in case the user scrolled the results before hiding them
      return this.itemsList.scrollTo(this.focussedIndex);
    }
  }

  hide(options = {}) {
    if (this.showing) {
      this.showing = false;
      this.el.hide();
      if (!options.silent) {
        return this._triggerEvent("hide");
      }
    }
  }

  destroy() {
    return this.el.remove();
  }

  // PRIVATE
  _selectItem(item) {
    this._selectedItem = item;
    return this._triggerEvent("select", item);
  }

  _setKeyHandler(keycode, callback, modifiers = {}) {
    return jQuery(document).keydown((event) => {
      if (event.isDefaultPrevented()) {
        return;
      } // Someone else has handled this interaction
      if (event.which !== keycode) {
        return;
      }
      if (Object.keys(modifiers).some((modifier) => event[modifier] !== modifiers[modifier])) {
        return;
      }
      return callback(event);
    });
  }

  _triggerEvent(name, ...payload) {
    return jQuery(this).triggerHandler(name, payload);
  }
};

Sparkle.Spotlight.prototype.isEmpty = true;

// Restore sub-modules (ItemsList, ItemDetails, StatusBar, etc.) that the
// imports above attached to the previous `Sparkle.Spotlight` namespace object
// before this file replaced it with the class.
if (__previousSpotlightNamespace) {
  Object.assign(Sparkle.Spotlight, __previousSpotlightNamespace);
}
